// Shared by the browser and server so leaderboard scores use the same rules.
const dictionary = new Set(require("./wordlist"));
const LETTER_POINTS = {
  A: 1,
  B: 3,
  C: 3,
  D: 2,
  E: 1,
  F: 4,
  G: 2,
  H: 4,
  I: 1,
  J: 8,
  K: 5,
  L: 1,
  M: 3,
  N: 1,
  O: 1,
  P: 3,
  Q: 10,
  R: 1,
  S: 1,
  T: 1,
  U: 1,
  V: 4,
  W: 4,
  X: 8,
  Y: 4,
  Z: 10,
};
const BONUS_CODES = {
  "00": "none",
  B1: "2xWord",
  B2: "3xWord",
  B3: "2xLetter",
  B4: "3xLetter",
  B5: "+10",
  B6: "+50",
};

function isValidWord(word) {
  return dictionary.has(word.toUpperCase());
}

function scoreCells(cells) {
  let letters = 0,
    multiplier = 1,
    bonus = 0;
  for (const cell of cells) {
    let letterMultiplier = 1;
    switch (cell.bonus) {
      case "2xWord":
        multiplier *= 2;
        break;
      case "3xWord":
        multiplier *= 3;
        break;
      case "2xLetter":
        letterMultiplier = 2;
        break;
      case "3xLetter":
        letterMultiplier = 3;
        break;
      case "+10":
        bonus += 10;
        break;
      case "+50":
        bonus += 50;
        break;
    }
    letters += (LETTER_POINTS[cell.letter] || 1) * letterMultiplier;
  }
  return letters * multiplier + bonus;
}

function calculateScore(board) {
  // As in the prototype, repeated words count once, at their best placement.
  const scores = new Map();
  const invalid = new Set();
  function collect(cells) {
    if (cells.length < 2) return;
    const word = cells.map((cell) => cell.letter).join("");
    if (!isValidWord(word)) {
      invalid.add(word);
      return;
    }
    scores.set(word, Math.max(scores.get(word) || 0, scoreCells(cells)));
  }
  for (const vertical of [false, true]) {
    for (let line = 0; line < board.length; line++) {
      let cells = [];
      for (let offset = 0; offset <= board.length; offset++) {
        const cell =
          offset === board.length
            ? null
            : vertical
              ? board[offset][line]
              : board[line][offset];
        if (cell?.letter) cells.push(cell);
        else {
          collect(cells);
          cells = [];
        }
      }
    }
  }
  const wordScores = [...scores].map(([word, score]) => ({ word, score }));
  return {
    score: wordScores.reduce((sum, entry) => sum + entry.score, 0),
    validWords: [...scores.keys()],
    invalidWords: [...invalid],
    wordScores,
    isValid: invalid.size === 0,
  };
}

function checkConnectivity(board) {
  const occupied = [];
  board.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (cell?.letter) occupied.push([r, c]);
    }),
  );
  if (!occupied.length) return true;
  const queue = [occupied[0]],
    visited = new Set();
  for (let i = 0; i < queue.length; i++) {
    const [r, c] = queue[i],
      key = `${r},${c}`;
    if (visited.has(key)) continue;
    visited.add(key);
    for (const [dr, dc] of [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ]) {
      if (
        board[r + dr]?.[c + dc]?.letter &&
        !visited.has(`${r + dr},${c + dc}`)
      )
        queue.push([r + dr, c + dc]);
    }
  }
  return visited.size === occupied.length;
}

function validateSubmission(seed, placements) {
  if (
    !seed ||
    typeof placements !== "string" ||
    !/^[A-Z.]{49}$/.test(placements)
  ) {
    throw new Error("A known seed and a 49-square board are required");
  }
  if (
    !/^[A-Z]{20}$/.test(seed.letters) ||
    typeof seed.board !== "string" ||
    seed.board.length !== 98
  )
    throw new Error("Invalid seed data");
  const remaining = [...seed.letters];
  const board = Array.from({ length: 7 }, (_, r) =>
    Array.from({ length: 7 }, (_, c) => {
      const index = r * 7 + c,
        letter = placements[index];
      const bonus = BONUS_CODES[seed.board.slice(index * 2, index * 2 + 2)];
      if (!bonus) throw new Error("Invalid seed bonus layout");
      if (letter !== ".") {
        const rackIndex = remaining.indexOf(letter);
        if (rackIndex < 0)
          throw new Error("Board uses letters not supplied by the seed");
        remaining.splice(rackIndex, 1);
      }
      return { letter: letter === "." ? null : letter, bonus };
    }),
  );
  if (!checkConnectivity(board))
    throw new Error("All placed letters must be connected");
  const result = calculateScore(board);
  if (!result.isValid || result.score <= 0 || !result.validWords.length)
    throw new Error("Board must contain valid connected words");
  return { ...result, placements, rack: seed.letters, bonusLayout: seed.board };
}

module.exports = {
  LETTER_POINTS,
  BONUS_CODES,
  isValidWord,
  calculateScore,
  checkConnectivity,
  validateSubmission,
};
